import { useState } from "react";
import SelectDatasets from "./SelectDatasets";
import UploadDatasets from "./UploadDatasets";

export default function DatasetsLayout({ datasets, setDatasets }) {
  const [activeTab, setActiveTab] = useState("view"); // Default to view datasets

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page Header */}
      <header className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-ktx-purple">
          Dataset Management
        </h1>
        <p className="mt-2 text-gray-600">
          View, upload, and manage datasets for fine-tuning your AI models
        </p>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm mb-8">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab("view")}
              className={`px-5 py-4 font-medium text-sm border-b-2 ${
                activeTab === "view"
                  ? "border-ktx-purple text-ktx-purple"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } focus:outline-none transition duration-150 ease-in-out`}
            >
              <div className="flex items-center">
                <svg 
                  className="mr-2 h-5 w-5" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 6h16M4 10h16M4 14h16M4 18h16" 
                  />
                </svg>
                View Datasets
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab("upload")}
              className={`ml-8 px-5 py-4 font-medium text-sm border-b-2 ${
                activeTab === "upload"
                  ? "border-ktx-purple text-ktx-purple"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } focus:outline-none transition duration-150 ease-in-out`}
            >
              <div className="flex items-center">
                <svg 
                  className="mr-2 h-5 w-5" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                  />
                </svg>
                Create Dataset
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Active Component */}
      <div className="transition-opacity duration-300">
        {activeTab === "view" ? (
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
            <h2 className="text-xl font-semibold mb-4">Available Datasets</h2>
            <SelectDatasets datasets={datasets} setDatasets={setDatasets} />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
            <h2 className="text-xl font-semibold mb-4">Upload New Dataset</h2>
            <UploadDatasets onUploadSuccess={() => setActiveTab("view")} />
          </div>
        )}
      </div>
      
      {/* Information section */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
          <h4 className="font-medium text-ktx-purple flex items-center mb-4">
            <svg className="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Dataset Best Practices
          </h4>
          <div className="text-sm text-gray-600 space-y-2">
            <p>For optimal fine-tuning results:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Include diverse examples that represent real-world usage</li>
              <li>Ensure high-quality data with minimal errors</li>
              <li>Use consistent formatting for similar types of examples</li>
              <li>Provide clear inputs and expected outputs</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
          <h4 className="font-medium text-ktx-purple flex items-center mb-4">
            <svg className="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Supported Formats
          </h4>
          <div className="text-sm text-gray-600 space-y-2">
            <p>We support the following dataset formats:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><span className="font-medium">JSONL</span> - Each line is a valid JSON object</li>
              <li><span className="font-medium">CSV</span> - With prompt and completion columns</li>
              <li><span className="font-medium">HuggingFace</span> - Datasets from the HF Hub</li>
              <li><span className="font-medium">Text Files</span> - For simple instruction-following tasks</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}